import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import language from 'config/es';
import env from 'config/env';
import { logout } from 'redux/login.duck';
import { logoutSessionAD } from 'api/sessionAD.helper';
import Button from 'shared/ui/Button/Button';
import Tip from 'shared/ui/Tip/Tip';
import Icon from 'shared/ui/icons/Icon';
import logoGloria from '../../assets/img/grupo-gloria.png';

interface SessionUser {
  id?: {
    user?: { name?: string; username?: string };
    role?: { name?: string };
  };
}

const readSessionUser = (): SessionUser | null => {
  try {
    return JSON.parse(sessionStorage.getItem('user') || 'null');
  } catch {
    return null;
  }
};

/*
 * El backend arma el nombre juntando nombres y apellidos, y cuando faltan llega
 * literalmente "undefined undefined": esas partes se descartan.
 */
const cleanName = (name?: string): string =>
  (name ?? '')
    .split(/\s+/)
    .filter((part) => part && part !== 'undefined' && part !== 'null')
    .join(' ');

const initialsOf = (name: string): string =>
  name
    .split(/[\s._@-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('') || 'U';

interface HeaderProps {
  collapsed: boolean;
  /** Bajo 768 px la barra lateral queda en iconos y no se puede expandir. */
  canToggle: boolean;
  onToggle: () => void;
}

const Header = ({ collapsed, canToggle, onToggle }: HeaderProps) => {
  const dispatch = useDispatch();
  const [user] = useState(readSessionUser);
  const [loggingOut, setLoggingOut] = useState(false);
  const name = cleanName(user?.id?.user?.name) || user?.id?.user?.username || 'Usuario';
  const role = user?.id?.role?.name ?? '';

  /*
   * Mismo cierre que tenía la barra anterior: registra la salida en el módulo
   * de seguridad y, responda o no, cierra la sesión de AD y vuelve al login.
   */
  const onLogout = () => {
    setLoggingOut(true);
    const json = {
      username: sessionStorage.getItem('username'),
      codeSystem: env.app.systemCode,
      registerLog: true,
    };
    // `logout` es un thunk: el dispatch sin tipos de redux no sabe que devuelve una promesa.
    const dispatchThunk = dispatch as unknown as (action: unknown) => Promise<unknown>;
    Promise.resolve(dispatchThunk(logout(json)))
      .catch((error: unknown) => console.log(error))
      .finally(() => logoutSessionAD('/login'));
  };

  const toggleLabel = collapsed ? 'Expandir menú' : 'Contraer menú';

  return (
    <header className="app-header">
      <div className="app-header__brand">
        {canToggle && (
          <Tip id="header-toggle" placement="bottom" tipo="nombre" label={toggleLabel}>
            <button
              type="button"
              className="app-header__toggle"
              aria-label={toggleLabel}
              aria-expanded={!collapsed}
              onClick={onToggle}
            >
              <Icon name={collapsed ? 'chevronsRight' : 'chevronsLeft'} size={18} />
            </button>
          </Tip>
        )}
        <img src={logoGloria} alt="Grupo Gloria" className="app-header__logo" />
        <span className="app-header__divider" aria-hidden="true" />
        <span className="app-header__title">{language.messages.title_system}</span>
      </div>

      <div className="app-header__end">
        <div className="app-header__user">
          <span className="app-avatar" aria-hidden="true">
            {initialsOf(name)}
          </span>
          <span className="app-header__who">
            <span className="app-header__name" title={name}>
              {name}
            </span>
            {role && (
              <span className="app-header__role" title={role}>
                {role}
              </span>
            )}
          </span>
        </div>
        <Button
          kind="secondary"
          size="toolbar"
          iconOnly
          icon={loggingOut ? 'refresh' : 'logout'}
          className={`app-logout${loggingOut ? ' is-busy' : ''}`}
          aria-label={loggingOut ? 'Cerrando sesión…' : 'Cerrar sesión'}
          onClick={onLogout}
          disabled={loggingOut}
          busy={loggingOut}
        />
      </div>
    </header>
  );
};

export default Header;
